import React, { useState } from "react";
import { PartyStore } from "../../store/PartyStore.js";
import { EventBus } from "../../engine/EventBus.js";

/**
 * U08 — formation rearrange screen.
 *
 * Renders the 4 formation slots in the same 2×2 arrangement the mobile
 * PartyBar uses — front row on top (indices 0, 1), back row below
 * (2, 3). Tap a slot to select it, tap a second slot to swap. Cancel
 * deselects.
 *
 * Gating: spec calls for this to be camp-cell only. Camp mechanics
 * aren't in-engine yet, so the menu entry is available whenever the
 * menu is open — U-series follow-up will gate it once camp exists.
 */
interface Props {
  onBack: () => void;
}

export function FormationScreen({ onBack }: Props): React.ReactElement {
  const [selected, setSelected] = useState<number | null>(null);
  const [members, setMembers] = useState(() => PartyStore.getMembers());
  const [voiceId, setVoiceId] = useState<string | null>(
    () => PartyStore.getVoice()?.instanceId ?? null,
  );

  const handleTap = (idx: number) => {
    // Tapping an empty slot is a no-op unless a filled slot is already
    // armed — in that case we swap the filled character into the empty
    // index, which effectively moves them. `PartyStore.rearrange`
    // guards against out-of-range indices, so the guard is here.
    if (idx >= members.length && selected === null) return;
    if (selected === null) {
      setSelected(idx);
      return;
    }
    if (selected === idx) {
      setSelected(null);
      return;
    }
    if (idx >= members.length) {
      setSelected(null);
      return;
    }
    PartyStore.rearrange(selected, idx);
    EventBus.emit("ui:party_update", {});
    setSelected(null);
    setMembers(PartyStore.getMembers());
  };

  const slot = (idx: number) => {
    const m = members[idx];
    const isSelected = selected === idx;
    return (
      <button
        key={idx}
        type="button"
        onClick={() => handleTap(idx)}
        style={{
          background: isSelected ? "#394150" : "rgba(0,0,0,0.65)",
          border: isSelected
            ? "2px solid #7dcfff"
            : "1px solid rgba(255,255,255,0.3)",
          borderRadius: 4,
          padding: "10px 12px",
          minHeight: 64,
          cursor: m || selected !== null ? "pointer" : "default",
          color: m ? "white" : "#777",
          fontFamily: "monospace",
          fontSize: 12,
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <span style={{ color: "#aaa", fontSize: 10 }}>
          {idx < 2 ? "Front" : "Back"} {idx % 2 === 0 ? "L" : "R"}
        </span>
        <span>{m ? m.name : "— empty —"}</span>
        {m && (
          <span style={{ color: "#888", fontSize: 10 }}>
            {m.characterClass} · {m.hp}/{m.maxHp} HP
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 320,
      }}
    >
      <h3
        style={{
          color: "white",
          fontFamily: "monospace",
          margin: 0,
          fontSize: 16,
          letterSpacing: 1,
          textAlign: "center",
        }}
      >
        FORMATION
      </h3>
      <div
        style={{
          color: "#aaa",
          fontFamily: "monospace",
          fontSize: 11,
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        {selected === null
          ? "Tap a character, then tap a slot to swap."
          : "Tap another slot to swap."}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "auto auto",
          gap: 8,
        }}
      >
        {slot(0)}
        {slot(1)}
        {slot(2)}
        {slot(3)}
      </div>
      <div
        style={{
          color: "#aaa",
          fontFamily: "monospace",
          fontSize: 11,
          textAlign: "center",
          marginTop: 4,
        }}
      >
        PARTY VOICE — speaks in dialogue and skill checks
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {members.map((m) => {
          const isVoice = voiceId === m.instanceId;
          return (
            <button
              key={m.instanceId}
              type="button"
              onClick={() => {
                PartyStore.setVoice(m.instanceId);
                setVoiceId(m.instanceId);
                EventBus.emit("ui:party_update", {});
              }}
              style={{
                flex: 1,
                background: isVoice ? "#394150" : "rgba(0,0,0,0.65)",
                border: isVoice
                  ? "2px solid #7dcfff"
                  : "1px solid rgba(255,255,255,0.3)",
                borderRadius: 4,
                padding: "6px 4px",
                color: isVoice ? "#7dcfff" : "white",
                fontFamily: "monospace",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {m.name.split(" ")[0]}
              {isVoice ? " ●" : ""}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: "#333",
          color: "white",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: 4,
          padding: "8px 24px",
          fontFamily: "monospace",
          fontSize: 13,
          cursor: "pointer",
          width: "100%",
        }}
      >
        Back
      </button>
    </div>
  );
}
